import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AUTH_DISABLED } from '@/lib/featureFlags';

type UserRole = 'admin' | 'moderator' | 'contributor' | 'user';

interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
}

type ViewAsRole = 'admin' | 'contributor' | 'user' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: UserRole[];
  isLoading: boolean;
  isAdmin: boolean;
  isContributor: boolean;
  isPremium: boolean;
  actualIsAdmin: boolean;
  viewAsRole: ViewAsRole;
  setViewAsRole: (role: ViewAsRole) => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_DISABLED) {
    const publicUser = {
      id: 'public',
      email: 'public@managertable.com',
      app_metadata: {},
      user_metadata: {},
      aud: '',
      created_at: new Date().toISOString(),
    } as unknown as User;

    return (
      <AuthContext.Provider value={{
        user: publicUser,
        session: null,
        profile: null,
        roles: [],
        isLoading: false,
        isAdmin: false,
        isContributor: false,
        isPremium: true,
        actualIsAdmin: false,
        viewAsRole: null,
        setViewAsRole: () => {},
        signIn: async () => ({ error: new Error('Authentication is disabled in this build') }),
        signUp: async () => ({ error: new Error('Authentication is disabled in this build') }),
        signOut: async () => {},
      }}>
        {children}
      </AuthContext.Provider>
    );
  }

  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<ViewAsRole>(null);

  const fetchProfile = async (userId: string) => {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return data as Profile | null;
  };

  const fetchRoles = async (userId: string) => {
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    return (data?.map(r => r.role) || []) as UserRole[];
  };

  const fetchSubscription = async (userId: string) => {
    const { data } = await supabase
      .from('user_subscriptions')
      .select('status, current_period_end')
      .eq('user_id', userId)
      .single();
    return data?.status === 'active' && 
      data?.current_period_end && 
      new Date(data.current_period_end) > new Date();
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer Supabase calls with setTimeout to prevent deadlock
          setTimeout(async () => {
            const [profileData, rolesData, premiumStatus] = await Promise.all([
              fetchProfile(session.user.id),
              fetchRoles(session.user.id),
              fetchSubscription(session.user.id)
            ]);
            setProfile(profileData);
            setRoles(rolesData);
            setIsPremium(!!premiumStatus);
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setIsPremium(false);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        Promise.all([
          fetchProfile(session.user.id),
          fetchRoles(session.user.id),
          fetchSubscription(session.user.id)
        ]).then(([profileData, rolesData, premiumStatus]) => {
          setProfile(profileData);
          setRoles(rolesData);
          setIsPremium(!!premiumStatus);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: { display_name: displayName }
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Compute real booleans (unaffected by viewAs)
  const actualIsAdmin = roles.includes('admin');
  const actualIsContributor = roles.includes('contributor') || actualIsAdmin;
  const actualIsPremium = isPremium || actualIsAdmin;

  // Apply viewAs override (only downgrades, never upgrades)
  const setViewAsRole = (role: ViewAsRole) => {
    if (!actualIsAdmin) return; // Only real admins can use this
    setViewAsRoleState(role);
  };

  let effectiveIsAdmin = actualIsAdmin;
  let effectiveIsContributor = actualIsContributor;
  let effectiveIsPremium = actualIsPremium;

  if (viewAsRole && actualIsAdmin) {
    if (viewAsRole === 'user') {
      effectiveIsAdmin = false;
      effectiveIsContributor = false;
      effectiveIsPremium = false;
    } else if (viewAsRole === 'contributor') {
      effectiveIsAdmin = false;
      effectiveIsContributor = true;
      effectiveIsPremium = false;
    }
    // viewAsRole === 'admin' → keep real values
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      roles,
      isLoading,
      isAdmin: effectiveIsAdmin,
      isContributor: effectiveIsContributor,
      isPremium: effectiveIsPremium,
      actualIsAdmin,
      viewAsRole,
      setViewAsRole,
      signIn,
      signUp,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
